// Customers > Add customer (issue #259): one form that makes the customer in
// Zoho Books, the Hub and the B2B wholesale site.

import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../lib/shadcn/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Switch } from '../../lib/shadcn/switch'
import { toast } from '../../lib/shadcn/sonner'
import { useAddCustomer } from '../../hooks/backend/customersPage'
import type { DeliveryMethod } from './CustomerEditDialog'

interface Form {
  company_name: string
  first_name: string
  last_name: string
  email: string
  billing_phone: string
  website: string
  billing_address: string
  billing_city: string
  billing_state: string
  billing_zip: string
  shipping_address: string
  shipping_city: string
  shipping_state: string
  shipping_zip: string
  shipping_phone: string
  delivery_method: DeliveryMethod
  b2b_role: 'bb-70' | 'bb-50' | 'none'
  store_locator: boolean
  send_b2b_email: boolean
}

const EMPTY: Form = {
  company_name: '',
  first_name: '',
  last_name: '',
  email: '',
  billing_phone: '',
  website: '',
  billing_address: '',
  billing_city: '',
  billing_state: 'TX',
  billing_zip: '',
  shipping_address: '',
  shipping_city: '',
  shipping_state: 'TX',
  shipping_zip: '',
  shipping_phone: '',
  delivery_method: 'shipping',
  b2b_role: 'bb-70',
  store_locator: false,
  send_b2b_email: false,
}

type TextField = Exclude<keyof Form, 'delivery_method' | 'b2b_role' | 'store_locator' | 'send_b2b_email'>

interface Result {
  customer_id: string
  contact_name: string
  zoho: 'created' | 'linked'
  b2b: 'created' | 'linked' | 'skipped' | 'error'
  b2b_email_sent: boolean
  warnings: string[]
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdded: (customerId: string) => void
}

export default function AddCustomerDialog({ open, onOpenChange, onAdded }: Props) {
  const addCustomer = useAddCustomer()
  const [form, setForm] = useState<Form>(EMPTY)
  const [sameAsBilling, setSameAsBilling] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(EMPTY)
      setSameAsBilling(true)
    }
  }, [open])

  const field = (key: TextField, label: string, props: { type?: string; placeholder?: string } = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`new-customer-${key}`}>{label}</Label>
      <Input
        id={`new-customer-${key}`}
        value={form[key]}
        type={props.type}
        placeholder={props.placeholder}
        disabled={saving}
        onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
      />
    </div>
  )

  const name = form.company_name.trim() || `${form.first_name} ${form.last_name}`.trim()
  const needsEmail = form.b2b_role !== 'none' && !form.email.trim()

  const handleSave = async () => {
    if (!name) {
      toast.error('Enter a company or contact name')
      return
    }
    setSaving(true)
    try {
      const shipping = sameAsBilling
        ? {
            shipping_address: form.billing_address,
            shipping_city: form.billing_city,
            shipping_state: form.billing_state,
            shipping_zip: form.billing_zip,
            shipping_phone: form.billing_phone,
          }
        : {
            shipping_address: form.shipping_address,
            shipping_city: form.shipping_city,
            shipping_state: form.shipping_state,
            shipping_zip: form.shipping_zip,
            shipping_phone: form.shipping_phone,
          }
      const result = (await addCustomer.trigger({
        company_name: form.company_name,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        website: form.website,
        billing_address: form.billing_address,
        billing_city: form.billing_city,
        billing_state: form.billing_state,
        billing_zip: form.billing_zip,
        billing_phone: form.billing_phone,
        ...shipping,
        delivery_method: form.delivery_method,
        store_locator: form.store_locator,
        b2b_role: form.b2b_role === 'none' ? null : form.b2b_role,
        send_b2b_email: form.b2b_role !== 'none' && form.send_b2b_email,
      })) as Result
      const parts = [result.zoho === 'linked' ? 'linked to its Zoho contact' : 'added to Zoho']
      if (result.b2b === 'created') {
        parts.push(result.b2b_email_sent ? 'B2B account made and password email sent' : 'B2B account made (no email sent)')
      }
      if (result.b2b === 'linked') parts.push('linked to its B2B account')
      toast.success(`${result.contact_name} added: ${parts.join(', ')}`)
      for (const w of result.warnings ?? []) toast.warning(w, { duration: 10000 })
      onOpenChange(false)
      onAdded(result.customer_id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add customer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!saving) onOpenChange(o) }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
          <DialogDescription>
            Adds the customer to the Hub and Zoho Books, and gives them a B2B wholesale account. They only get an email if you tick the password email box.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {field('company_name', 'Company Name')}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('first_name', 'First Name')}
            {field('last_name', 'Last Name')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('email', 'Email', { type: 'email' })}
            {field('billing_phone', 'Phone', { type: 'tel' })}
          </div>
          {field('website', 'Website')}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-customer-b2b">B2B wholesale site</Label>
              <Select
                value={form.b2b_role}
                onValueChange={v => setForm(prev => ({ ...prev, b2b_role: v as Form['b2b_role'] }))}
              >
                <SelectTrigger id="new-customer-b2b">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bb-70">bb-70 price group (most accounts)</SelectItem>
                  <SelectItem value="bb-50">bb-50 price group</SelectItem>
                  <SelectItem value="none">Don't add to B2B</SelectItem>
                </SelectContent>
              </Select>
              {needsEmail ? (
                <p className="text-xs text-destructive">A B2B account needs an email address.</p>
              ) : null}
              {form.b2b_role !== 'none' ? (
                <label className="flex items-start gap-2 text-sm pt-1">
                  <Checkbox
                    className="mt-0.5"
                    checked={form.send_b2b_email}
                    onCheckedChange={v => setForm(prev => ({ ...prev, send_b2b_email: v === true }))}
                  />
                  <span>Email them a link to set their B2B password</span>
                </label>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-customer-delivery">Delivery Method</Label>
              <Select
                value={form.delivery_method}
                onValueChange={v => setForm(prev => ({ ...prev, delivery_method: v as DeliveryMethod }))}
              >
                <SelectTrigger id="new-customer-delivery">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shipping">Shipping</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="free_delivery">Free Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <h3 className="text-sm font-medium text-foreground">Billing Address</h3>
            {field('billing_address', 'Street Address')}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {field('billing_city', 'City')}
              {field('billing_state', 'State')}
              {field('billing_zip', 'Zip')}
            </div>
          </div>

          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-medium text-foreground">Shipping Address</h3>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={sameAsBilling} onCheckedChange={v => setSameAsBilling(v === true)} />
                Same as billing
              </label>
            </div>
            {sameAsBilling ? null : (
              <>
                {field('shipping_address', 'Street Address')}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {field('shipping_city', 'City')}
                  {field('shipping_state', 'State')}
                  {field('shipping_zip', 'Zip')}
                </div>
                {field('shipping_phone', 'Shipping Phone', { type: 'tel' })}
              </>
            )}
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div className="space-y-1">
              <Label htmlFor="new-customer-locator">Show on Web store locator</Label>
              <p className="text-xs text-muted-foreground">
                Lists them on the poppinwtx.com store locator with their address and website.
              </p>
            </div>
            <Switch
              id="new-customer-locator"
              checked={form.store_locator}
              onCheckedChange={v => setForm(prev => ({ ...prev, store_locator: v }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving || !name || needsEmail}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Adding…
              </>
            ) : (
              'Add Customer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
